import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import ContentCard from '../../../../../../components/layout/ContentCard/ContentCard';
import Table from '../../../../../../components/data/Table/Table';
import SearchFilter from '../../../../../../components/data/SearchFilter';
import Pagination from '../../../../../../components/data/Pagination/Pagination';
import Badge from '../../../../../../components/common/Badge/Badge';
import Button from '../../../../../../components/common/Button/Button';
import { ConfirmModal } from '../../../../../../components/common/ConfirmModal';
import { translate, getFormatDate } from '../../../../../../utils/Common';
import { getClientConfRoleInfo } from '../../../../../../utils/modules/clients';
import { usePlatforms } from '../../../../../../hooks/usePlatforms';
import { verifyAccessViewPermissions } from '../../../../../../config/permissions';
import {
  formatClientName,
  formatAccountName,
} from '../../utils/companyFormatters';
import { getStatusVariant } from '../../utils/companyHelpers';
import { FaTrash, FaUserPlus } from 'react-icons/fa';
import AddClientModal from '../AddClientModal';
import BtnSync from '../../../../../../components/BtnSync';
import styles from './ClientsTab.module.css';

const ClientsTab = ({ company, showSuccess, showError, onClientAdded }) => {
  const { companyId } = useParams();
  const history = useHistory();
  const permissionsPath = "/dashboard/companies";
  const canUpdate = verifyAccessViewPermissions(["update"], { path: permissionsPath });
  const canDelete = verifyAccessViewPermissions(["delete"], { path: permissionsPath });
  // Sincronizar la asociación del cliente con esta compañía: misma acción `sync` del módulo
  // /dashboard/companies que usa el listado (el endpoint solo existe en el api de USA).
  const canSync = verifyAccessViewPermissions(["sync"], { path: permissionsPath });
  const { getAssignedMembersById, removeClientFromCompany } = usePlatforms();
  const [clients, setClients] = useState([]);
  // Starts true: between the first render and the effect firing, a false
  // flag with an empty list paints the "nothing here" state for a frame,
  // which reads as the screen finding nothing and then changing its mind.
  const [clientsLoading, setClientsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [deletingClientId, setDeletingClientId] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const clientsAbortControllerRef = useRef(null);

  useEffect(() => {
    if (!companyId) return;

    const loadClients = async () => {
      setClientsLoading(true);

      if (clientsAbortControllerRef.current) {
        clientsAbortControllerRef.current.abort();
      }

      const liveController = new AbortController();
      clientsAbortControllerRef.current = liveController;

      try {
        // Cargar todos los datos para filtrar localmente
        const result = await getAssignedMembersById(
          companyId,
          1,
          1000, // Cargar muchos para filtrar localmente
          clientsAbortControllerRef.current.signal
        );

        // Manejar diferentes formatos de respuesta
        let clientsList = [];
        let total = 0;

        // Estructura: { code, message, total, members: [...] }
        if (result?.members && Array.isArray(result.members)) {
          clientsList = result.members;
          total = result.total || result.members.length;
        } else if (Array.isArray(result)) {
          clientsList = result;
          total = result.length;
        } else if (result?.payload) {
          if (Array.isArray(result.payload)) {
            clientsList = result.payload;
            total = result.total || result.payload.length;
          } else if (result.payload.items) {
            clientsList = result.payload.items || [];
            total = result.payload.total || result.total || clientsList.length;
          }
        } else if (result?.data) {
          clientsList = Array.isArray(result.data) ? result.data : [];
          total = result.total || clientsList.length;
        } else if (result?.items) {
          clientsList = result.items || [];
          total = result.total || clientsList.length;
        }

        setClients(clientsList);
        setTotalItems(total);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error loading clients:', err);
          if (showError) {
            showError(err.message || translate('dashboard.companies.detail.clients.errors.loadError'));
          }
          setClients([]);
        }
      } finally {
        // Only the request that was not superseded may clear the flag: an
        // aborted one used to switch it off for its replacement, blanking the
        // view until the new data arrived.
        if (!liveController.signal.aborted) setClientsLoading(false);
      }
    };

    loadClients();

    return () => {
      if (clientsAbortControllerRef.current) {
        clientsAbortControllerRef.current.abort();
      }
    };
  }, [companyId, getAssignedMembersById, removeClientFromCompany]);

  // Filtrar clients basado en búsqueda y filtros
  const filteredClients = useMemo(() => {
    let result = [...clients];

    // Aplicar búsqueda general
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(client => {
        const userId = (client.userId || client.id || '').toString().toLowerCase();
        const name = formatClientName(client).toLowerCase();
        const phone = (client.phone || '').toString().toLowerCase();
        
        return userId.includes(searchLower) ||
               name.includes(searchLower) ||
               phone.includes(searchLower);
      });
    }

    // Aplicar filtros específicos
    if (filters.role && filters.role !== '') {
      const roleFilter = parseInt(filters.role);
      if (!isNaN(roleFilter)) {
        result = result.filter(client => {
          // Priorizar IdConfRoles sobre otros campos
          const roleId = client?.IdConfRoles ?? client?.idconfroles ?? client?.IdRole ?? client?.registrationRole;
          return roleId === roleFilter;
        });
      }
    }

    if (filters.status !== undefined && filters.status !== '') {
      const statusFilter = parseInt(filters.status);
      if (!isNaN(statusFilter)) {
        result = result.filter(client => {
          const isActive = client?.status === 1;
          return (statusFilter === 1 && isActive) || (statusFilter === 0 && !isActive);
        });
      }
    }

    return result;
  }, [clients, searchTerm, filters]);

  // Paginación sobre resultados filtrados
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedClients = filteredClients.slice(startIndex, endIndex);
  const filteredTotalPages = Math.ceil(filteredClients.length / itemsPerPage);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleFilter = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const handleRemoveClientClick = (client) => {
    if (!canDelete) return;
    setClientToDelete(client);
    setConfirmModalOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!company || !clientToDelete) return;
    if (!canDelete) return;
    
    const companyCode = company?.companyCode || company?.CodUser;
    if (!companyCode) {
      if (showError) {
        showError(translate('dashboard.companies.detail.errors.companyCodeNotFound'));
      }
      setConfirmModalOpen(false);
      setClientToDelete(null);
      return;
    }

    const clientId = clientToDelete?.userId || clientToDelete?.id || clientToDelete?.IdUsuario;
    if (!clientId) {
      if (showError) {
        showError(translate('dashboard.companies.detail.clients.errors.clientIdNotFound'));
      }
      setConfirmModalOpen(false);
      setClientToDelete(null);
      return;
    }

    const clientName = formatClientName(clientToDelete);
    setDeletingClientId(clientId);
    setConfirmModalOpen(false);

    // Función para obtener el mensaje de error detallado según el código del API
    const getErrorMessage = (message) => {
      if (!message) return translate('dashboard.companies.detail.clients.errors.removeError');
      
      const errorMessages = {
        'USER_ID_DOESNT_EXISTS': translate('dashboard.companies.detail.clients.errors.userIdDoesntExists'),
        'COMPANY_DOESNT_EXISTS': translate('dashboard.companies.detail.clients.errors.companyDoesntExists'),
        'ASSIGNMENT_DOESNT_EXISTS': translate('dashboard.companies.detail.clients.errors.assignmentDoesntExists'),
        'ASSIGN_ANOTHER_COMPANY_BEFORE_DELETE_THIS_ONE': translate('dashboard.companies.detail.clients.errors.assignAnotherCompanyBeforeDelete'),
      };
      return errorMessages[message] || translate('dashboard.companies.detail.clients.errors.removeError');
    };

    try {
      const abortController = new AbortController();
      const result = await removeClientFromCompany(clientId, companyCode, abortController.signal);
      
      if (result && (result.code === 200 || result.message === 'ASSIGNMENT_REMOVED')) {
        // Recargar la lista de clientes
        const clientsResult = await getAssignedMembersById(
          companyId,
          1,
          1000,
          abortController.signal
        );

        let clientsList = [];
        let total = 0;

        if (clientsResult?.members && Array.isArray(clientsResult.members)) {
          clientsList = clientsResult.members;
          total = clientsResult.total || clientsResult.members.length;
        } else if (Array.isArray(clientsResult)) {
          clientsList = clientsResult;
          total = clientsResult.length;
        } else if (clientsResult?.payload) {
          if (Array.isArray(clientsResult.payload)) {
            clientsList = clientsResult.payload;
            total = clientsResult.total || clientsResult.payload.length;
          } else if (clientsResult.payload.items) {
            clientsList = clientsResult.payload.items || [];
            total = clientsResult.payload.total || clientsResult.total || clientsList.length;
          }
        } else if (clientsResult?.data) {
          clientsList = Array.isArray(clientsResult.data) ? clientsResult.data : [];
          total = clientsResult.total || clientsList.length;
        } else if (clientsResult?.items) {
          clientsList = clientsResult.items || [];
          total = clientsResult.total || clientsList.length;
        }

        setClients(clientsList);
        setTotalItems(total);
        
        // Actualizar el contador en las tabs del componente padre
        if (onClientAdded) {
          onClientAdded();
        }
        
        // Mostrar mensaje de éxito
        if (showSuccess) {
          showSuccess(
            translate('dashboard.companies.detail.clients.success.removeSuccess').replace('{clientName}', clientName)
          );
        }
      } else {
        // Mostrar mensaje de error detallado según el código del API
        if (showError) {
          const errorMessage = getErrorMessage(result?.message);
          showError(errorMessage);
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error removing client from company:', err);
        if (showError) {
          // Intentar extraer el mensaje del error si viene del API
          const errorMessage = getErrorMessage(err.message);
          showError(errorMessage);
        }
      }
    } finally {
      setDeletingClientId(null);
      setClientToDelete(null);
    }
  };

  const handleCancelRemove = () => {
    setConfirmModalOpen(false);
    setClientToDelete(null);
  };

  const handleRowClick = (row) => {
    const clientId = row?.clientId;

    if (clientId) {
      history.push(`/dashboard/clients/details/${clientId}`);
    }
  };

  const columns = [
    {
      key: 'userId',
      label: translate('dashboard.companies.detail.clients.columns.userId'),
      sortable: true,
      width: '200px',
      render: (row) => row?.userId || '-'
    },
    {
      key: 'name',
      label: translate('dashboard.companies.detail.clients.columns.name'),
      sortable: true,
      width: '200px',
      render: (row) => formatClientName(row)
    },
    {
      key: 'phone',
      label: translate('dashboard.companies.detail.clients.columns.phone'),
      sortable: true,
      width: '150px',
      render: (row) => row?.phone || '-'
    },
    {
      key: 'accesibility',
      label: translate('dashboard.companies.detail.clients.columns.accessibility'),
      width: '120px',
      render: (row) => {
        const accesibility = row?.accesibility ?? row?.Accesibilidad;
        const isEnabled = accesibility === 1 || accesibility === true;
        return (
          <Badge variant={isEnabled ? 'success' : 'warning'}>
            {isEnabled ? translate('global.enabled') : translate('global.disabled')}
          </Badge>
        );
      }
    },
    {
      key: 'idconfroles',
      label: translate('dashboard.companies.detail.clients.columns.role'),
      width: '150px',
      render: (row) => {
        // Priorizar IdConfRoles sobre otros campos
        const roleId = row?.IdConfRoles ?? row?.idconfroles ?? row?.IdRole ?? row?.registrationRole;
        if (!roleId) return '-';
        
        const { label, icon } = getClientConfRoleInfo(roleId);
        
        return (
          <Badge variant="neutral" icon={icon} showIcon={true}>
            {label}
          </Badge>
        );
      }
    },
    {
      key: 'status',
      label: translate('dashboard.companies.detail.clients.columns.status'),
      width: '100px',
      render: (row) => {
        const isActive = row?.status === 1;
        return (
          <Badge variant={getStatusVariant(isActive)}>
            {isActive ? translate('dashboard.companies.status.active') : translate('dashboard.companies.status.inactive')}
          </Badge>
        );
      }
    },
    {
      key: 'actions',
      label: '',
      width: '100px',
      sortable: false,
      render: (row) => {
        const clientId = row?.userId || row?.id || row?.IdUsuario;
        const isDeleting = deletingClientId === clientId;

        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
            {canSync && row?.assignmentId && (
              <span
                title={translate('dashboard.companies.detail.clients.syncTooltip')}
                onClick={(e) => e.stopPropagation()}
              >
                <BtnSync
                  id={`btn-sync-assignment-${row.assignmentId}`}
                  mode="assignment"
                  assignmentId={row.assignmentId}
                  size="sm"
                  variant="btn"
                />
              </span>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveClientClick(row);
                }}
                disabled={isDeleting}
                title={translate('dashboard.companies.detail.clients.removeTooltip')}
                style={{
                  background: isDeleting ? 'var(--bg-disabled)' : 'var(--badge-error-bg)',
                  color: isDeleting ? 'var(--text-disabled)' : 'var(--badge-error-text)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '28px',
                  height: '28px',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  padding: 0
                }}
                onMouseEnter={(e) => {
                  if (!isDeleting) {
                    e.currentTarget.style.background = 'var(--color-error)';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDeleting) {
                    e.currentTarget.style.background = 'var(--badge-error-bg)';
                    e.currentTarget.style.color = 'var(--badge-error-text)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}
              >
                <FaTrash size={12} style={{ opacity: isDeleting ? 0.5 : 1 }} />
              </button>
            )}
          </div>
        );
      }
    }
  ];

  const handleClientAdded = () => {
    // Recargar la lista de clientes
    // El useEffect se ejecutará automáticamente cuando cambie companyId
    // Pero también podemos forzar una recarga aquí si es necesario
    if (clientsAbortControllerRef.current) {
      clientsAbortControllerRef.current.abort();
    }
    clientsAbortControllerRef.current = new AbortController();
    
    const loadClients = async () => {
      setClientsLoading(true);
      try {
        const result = await getAssignedMembersById(
          companyId,
          1,
          1000,
          clientsAbortControllerRef.current.signal
        );

        let clientsList = [];
        let total = 0;

        if (result?.members && Array.isArray(result.members)) {
          clientsList = result.members;
          total = result.total || result.members.length;
        } else if (Array.isArray(result)) {
          clientsList = result;
          total = result.length;
        } else if (result?.payload) {
          if (Array.isArray(result.payload)) {
            clientsList = result.payload;
            total = result.total || result.payload.length;
          } else if (result.payload.items) {
            clientsList = result.payload.items || [];
            total = result.payload.total || result.total || clientsList.length;
          }
        } else if (result?.data) {
          clientsList = Array.isArray(result.data) ? result.data : [];
          total = result.total || clientsList.length;
        } else if (result?.items) {
          clientsList = result.items || [];
          total = result.total || clientsList.length;
        }

        setClients(clientsList);
        setTotalItems(total);
        
        // Actualizar el contador en las tabs del componente padre
        if (onClientAdded) {
          onClientAdded();
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error loading clients:', err);
        }
      } finally {
        setClientsLoading(false);
      }
    };
    
    loadClients();
  };

  return (
    <div>
      <div className={styles.filtersContainer}>
        <div className={styles.searchFilterWrapper}>
          <SearchFilter
            placeholder={translate('dashboard.companies.detail.clients.filters.searchPlaceholder')}
            onSearch={handleSearch}
            filters={[
              { 
                type: 'select', 
                name: 'role', 
                placeholder: translate('dashboard.companies.detail.clients.filters.role'),
                options: [
                  { value: '1', label: translate('dashboard.companies.detail.clients.roles.customer') },
                  { value: '2', label: translate('dashboard.companies.detail.clients.roles.deliveryAgent') },
                  { value: '3', label: translate('dashboard.companies.detail.clients.roles.admin') }
                ]
              },
              { 
                type: 'select', 
                name: 'status', 
                placeholder: translate('dashboard.companies.detail.clients.filters.status'),
                options: [
                  { value: '1', label: translate('dashboard.companies.status.active') },
                  { value: '0', label: translate('dashboard.companies.status.inactive') }
                ]
              }
            ]}
            onFilter={handleFilter}
            activeFilters={filters}
          />
        </div>
        <div className={styles.addButtonWrapper}>
          {canUpdate && (
            <Button
              variant="primary"
              onClick={() => setIsAddModalOpen(true)}
              leftIcon={<FaUserPlus />}
              fullWidth
            >
              {translate('dashboard.companies.detail.clients.addButton')}
            </Button>
          )}
        </div>
      </div>

      <Table
        columns={columns}
        data={paginatedClients}
        loading={clientsLoading}
        emptyMessage={translate('dashboard.companies.detail.clients.empty')}
        hoverable
        onRowClick={handleRowClick}
      />

      {!clientsLoading && filteredClients.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={filteredTotalPages}
          totalItems={filteredClients.length}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModalOpen}
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        title={translate('dashboard.companies.detail.clients.confirmRemoveTitle')}
        message={
          clientToDelete
            ? translate('dashboard.companies.detail.clients.confirmRemove').replace('{clientName}', formatClientName(clientToDelete))
            : ''
        }
        confirmText={translate('dashboard.companies.detail.clients.confirmButton')}
        cancelText={translate('global.cancel')}
        variant="danger"
        isLoading={deletingClientId !== null}
      />

      {/* Add Client Modal */}
      <AddClientModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        company={company}
        showSuccess={showSuccess}
        showError={showError}
        onClientAdded={handleClientAdded}
        canUpdate={canUpdate}
      />
    </div>
  );
};

export default ClientsTab;

